import React, { useMemo } from "react";
import { SxProps, Stack, Link, FormControlLabel, Checkbox, Typography } from "@mui/material";
import ConfigOrderContentsRow from "../ConfigOrderContentsRow/config-order-layer-contents-row";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { DbOrderBasketItemInfos } from "../../../../Core/Api/Types/db-order-basket-item-infos";
import { useSelector } from "react-redux";
import {
  GetBasketSelectedItemIds,
  RemoveItem,
  SelectItemOnBasket,
  SelectItemsOnBasket,
  UnselectItemOnBasket,
  UnselectItemsOnBasket,
} from "../../../../Redux/Reducers/redux-slice-basket";
import { useAppDispatch } from "../../../../Redux/redux-store";
import { useTranslation } from "react-i18next";

export type ConfigOrderLayerContentsProps = {
  sx?: SxProps;
  title: string;
  items: DbOrderBasketItemInfos[];
  layer: ObjectLayer;
  groupId: number;
  onApplyToAll: (srcItem: DbOrderBasketItemInfos, copiedItems: DbOrderBasketItemInfos[]) => void;
};

const ConfigOrderLayerContents: React.FC<ConfigOrderLayerContentsProps> = (props) => {
  const { t } = useTranslation();
  const { items, layer, groupId, onApplyToAll } = props;
  const selectedIds = useSelector(GetBasketSelectedItemIds);
  const dispatch = useAppDispatch();

  const allChecked = useMemo((): boolean => {
    if (!selectedIds || selectedIds.length === 0) return false;
    let result: boolean = true;

    items.forEach((item) => {
      if (!selectedIds.includes(item.id)) {
        result = false;
        return;
      }
    });

    return result;
  }, [items, selectedIds]);

  const selectedIdsOfGroup = useMemo((): number[] => {
    if (!selectedIds || selectedIds.length === 0) return [];

    let result: number[] = [];
    items.forEach((item) => {
      if (selectedIds.includes(item.id)) result.push(item.id);
    });

    return result;
  }, [items, selectedIds]);

  const onCheckChange = (id: number) => {
    if (!selectedIds || !selectedIds.includes(id)) dispatch(SelectItemOnBasket(id));
    else dispatch(UnselectItemOnBasket(id));
  };

  const onAllCheckedChange = () => {
    if (!allChecked) dispatch(SelectItemsOnBasket(items.map((item) => item.id)));
    else dispatch(UnselectItemsOnBasket(items.map((item) => item.id)));
  };

  const onRemove = () => {
    if (selectedIdsOfGroup.length === 0) {
      items.map((item) => {
        dispatch(RemoveItem(item.id));
      });
    } else
      selectedIdsOfGroup.map((id) => {
        dispatch(RemoveItem(id));
      });
  };

  return (
    <Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <FormControlLabel
          onChange={onAllCheckedChange}
          control={<Checkbox checked={allChecked} />}
          label={
            <Typography variant="h6" component="span" color="text.secondary">
              {props.title}
            </Typography>
          }
          sx={{ p: "5px", ml: 0 }}
        />
        <Link component="button" underline="hover" color="error" onClick={onRemove}>
          {selectedIdsOfGroup.length > 0 ? t("button:removeSelected") : t("button:removeAllLayers", { layer })}
        </Link>
      </Stack>
      <Stack spacing={0.5}>
        {items.map((item) => (
          <ConfigOrderContentsRow
            key={item.id}
            id={item.id}
            title={item.name!}
            item={item}
            onChange={(id) => onCheckChange(id)}
            onApplyToAll={onApplyToAll}
            checked={selectedIds.includes(item.id)}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export default ConfigOrderLayerContents;
